// AppUser.model.ts
import * as XLSX from 'xlsx';
import { HouseFlat } from "./house-flat.model";
import { StreetBuilding } from "./street-building.model";
import { IUserProperty, UserProperty } from "./user-property.model";

export interface IAppUser {
  uid: string;
  profile: string;
  names: string;
  paternalLastName: string;
  maternalLastName: string;
  username: string;
  email: string;
  password?: string;
  temporaryPassword: boolean;
  auxPassword?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  /* OPCIONAL a propósito: solo GET /users/{uid} la manda. GET /account —el que
     usa AuthService.getUser()— no trae la llave y debe seguir funcionando. */
  properties?: IUserProperty[];
}

export class AppUser {
  uid: string;
  profile: string;
  names: string;
  paternalLastName: string;
  maternalLastName: string;
  username: string;
  email: string;
  password?: string;
  temporaryPassword: boolean;
  auxPassword?: string;
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  /* `undefined` = la respuesta no trae la llave, o sea que NO se sabe (caso de
     GET /account). `[]` = el backend contestó que el usuario no es residente de
     ninguna propiedad, que es lo normal en sudos, administradores y vigilantes.
     Son dos cosas distintas y la pantalla de detalle necesita distinguirlas. */
  properties?: UserProperty[];

  constructor(data: IAppUser) {
    this.profile = data.profile;
    this.names = data.names;
    this.paternalLastName = data.paternalLastName;
    this.maternalLastName = data.maternalLastName;
    this.uid = data.uid;
    this.username = data.username;
    this.email = data.email;
    this.password = data.password;
    this.temporaryPassword = data.temporaryPassword;
    this.auxPassword = data.auxPassword;
    this.lastLoginAt = data.lastLoginAt
      ? new Date(data.lastLoginAt)
      : undefined;
    this.createdAt = new Date(data.createdAt);
    this.updatedAt = new Date(data.updatedAt);
    this.properties = data.properties
      ? data.properties.map(item => new UserProperty(item))
      : undefined;
  }

  get fullName(): string {

    let fullName = '';

    if(this.names != '') {
      fullName = `${this.names} ${this.paternalLastName} ${this.maternalLastName}`;
    } else {
      fullName = this.username;
    }

    return fullName;
  }

  /* `fullName` cae al username cuando no hay nombre capturado, y por eso no sirve
     para decidir si HAY nombre. `personName` responde solo eso: el nombre de la
     persona, o cadena vacía si nunca se capturó. */
  get personName(): string {

    const parts: string[] = [
      this.names,
      this.paternalLastName,
      this.maternalLastName
    ].filter(part => !!part && part.trim() !== '');

    return parts.join(' ').trim();
  }

  get hasEmail(): boolean {
    return !!this.email && this.email.trim() !== '';
  }

  /* Nunca ha entrado no es un error: es un usuario recién dado de alta. Se pinta
     en gris y con texto, jamás en rojo. */
  get hasLoggedIn(): boolean {
    return !!this.lastLoginAt;
  }

  get profileLabel(): string {
    return this.isSudo
      ? 'Superadministrador'
      : 'Usuario';
  }

  get isSudo(): boolean {
    return this.profile === 'sudo';
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR downloadData                            */
  /* -------------------------------------------------------------------------- */
  // region downloadData
  downloadData(
    streetBuilding: StreetBuilding,
    houseFlat: HouseFlat
  ) {

    const headers = [
      'Usuario', 
      'Contraseña',
      'Calle',
      'Número'
    ];

    // Filas de datos
    const rows = [
      [
        this.username, 
        this.auxPassword || this.password,
        streetBuilding?.name,
        houseFlat?.number
      ]
    ];

    // Combinar encabezados y filas
    const data = [headers, ...rows];

    // Crear una hoja de Excel
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    // Ajustar el ancho de las columnas automáticamente
    const columnWidths = headers.map(header => ({ wch: header.length + 5 })); // Ajusta +5 para un espacio adicional
    worksheet['!cols'] = columnWidths;

    // Crear un libro de trabajo y agregar la hoja
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

    // Exportar el archivo XLSX
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Crear un Blob para la descarga
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

    // Crear un enlace de descarga
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);

    // Asignar el nombre del archivo
    link.download = `user-${streetBuilding?.name}-${houseFlat?.number}.xlsx`;

    // Disparar la descarga
    link.click();

  }

}
